import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import DetailItem from "../DetailItem";
import LeadSourceInlineFields from "./LeadSourceInlineFields";
import { useInlineFieldEdit } from "../InlineEdit/useInlineFieldEdit";
import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/date";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useViewLeadFieldVisibility } from "@/features/leads/hook/useViewLeadFieldVisibility";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";

/**
 * The Meta ad set / ad (the creative) the lead's ORIGINAL submission came
 * through — `lead.metaAdset` / `lead.metaAd` on the single-lead payload
 * (`GET /leads/:id`), null for leads that didn't come through a Meta ad.
 * First-touch, like the campaign: a re-enquiry through another ad never
 * changes it.
 */
interface LeadMetaAdRef {
  id: number;
  /** Ads Manager name — null until Meta reported one. */
  name: string | null;
  metaId: string;
}

/** Ads Manager name, or Meta's id when the name isn't known yet. */
const metaAdName = (ref: LeadMetaAdRef, noun: string) =>
  ref.name?.trim() || `${noun} ${ref.metaId}`;

export default function LeadSourceViewMode() {
  const avatarColors = [
    { bg: "#e1e4ea", text: "#0e121b" },
    { bg: "#c0d5ff", text: "#122368" },
    { bg: "#ffecc0", text: "#624c18" },
  ];

  const { lead, activeData, isChildActive, child } = useActiveDataMutation();
  const { canInlineEdit } = useInlineFieldEdit();
  const { isFieldVisible } = useViewLeadFieldVisibility();
  // Tenant-wide field order (arranged by dragging in edit mode; the keys
  // `source` / `campaign` are shared with the edit card, the rest are
  // view-only cells that follow behind them).
  const sourceOrder = useLeadViewFieldOrder("source");

  const metaAdset: LeadMetaAdRef | null = lead?.metaAdset ?? null;
  const metaAd: LeadMetaAdRef | null = lead?.metaAd ?? null;

  const leadUsers = lead?.users || [];
  const childUsers = (child?.childUsers ?? [])
    .map((u: any) => u?.user)
    .filter(Boolean);

  const uniqueUsersMap = new Map();
  leadUsers.forEach((u: any) => {
    if (u?.id) uniqueUsersMap.set(u.id, u);
  });
  if (isChildActive) {
    childUsers.forEach((u: any) => {
      if (u?.id) uniqueUsersMap.set(u.id, u);
    });
  }
  const assignedUsers = Array.from(uniqueUsersMap.values());

  // "Lead Created" date, made robust against two failure modes that both
  // surface as TODAY's date on the card:
  //   1. `createdAt` missing/invalid → `formatDate(undefined)` falls back to
  //      `new Date()` and renders the current time.
  //   2. A bad stored `createdAt` that is later than the lead's own
  //      `updatedAt` — impossible for a real row (you can't update something
  //      before it exists), seen on legacy/imported leads.
  // In both cases we prefer the lead's `updatedAt` (the most reliable
  // "this lead existed by" timestamp); healthy leads — where
  // createdAt <= updatedAt — are unaffected and show their true createdAt.
  const leadCreatedDisplay = (() => {
    const created = lead?.createdAt ? new Date(lead.createdAt) : null;
    const updated = lead?.updatedAt ? new Date(lead.updatedAt) : null;
    const createdOk = created && !Number.isNaN(created.getTime());
    const updatedOk = updated && !Number.isNaN(updated.getTime());
    if (createdOk && (!updatedOk || created.getTime() <= updated.getTime())) {
      return formatDate(lead.createdAt);
    }
    return updatedOk ? formatDate(lead.updatedAt) : "N/A";
  })();

  // Display-only hide (Settings → Manage → View Lead Display). Edit mode
  // renders LeadSourceEditMode instead, which is exempt.
  if (!isFieldVisible("leadSource")) return null;

  // `wide` cells take a whole row: Meta campaign / ad set / ad names run
  // 40–60 characters, and in a ~160px column they wrapped to three or four
  // lines and broke mid-word ("SKY_UK_LEADGEN_KER" / "ALA_21-…"). On their
  // own row they read on one line in the desktop drawer and wrap cleanly on
  // a phone, and the attribution reads top-down like Ads Manager (campaign
  // → ad set → ad). The short cells keep packing into the grid.
  const cells: {
    key: string;
    show: boolean;
    wide?: boolean;
    node: ReactNode;
  }[] = sourceOrder.sortByOrder(
    [
      {
        key: "source",
        show: isFieldVisible("source.source"),
        node: canInlineEdit ? (
          <LeadSourceInlineFields
            sourceName={lead?.leadSource?.name}
            sourceId={lead?.leadSource?.id}
            showCampaign={false}
          />
        ) : (
          <DetailItem label="Source" value={lead?.leadSource?.name} />
        ),
      },
      {
        key: "campaign",
        show: isFieldVisible("source.campaign"),
        wide: true,
        node: canInlineEdit ? (
          <LeadSourceInlineFields
            campaignName={lead?.campaign?.name}
            campaignId={lead?.campaign?.id}
            showSource={false}
          />
        ) : (
          <DetailItem label="Campaign" value={lead?.campaign?.name} />
        ),
      },
      {
        // The Meta ad set + ad (the creative — usually the video) the lead
        // first came through. Read-only (attribution, not an editable
        // field), and only rendered when the lead has one.
        key: "adset",
        show: isFieldVisible("source.adset") && !!metaAdset,
        wide: true,
        node: metaAdset ? (
          <DetailItem label="Ad set" value={metaAdName(metaAdset, "Ad set")} />
        ) : null,
      },
      {
        key: "ad",
        show: isFieldVisible("source.ad") && !!metaAd,
        wide: true,
        node: metaAd ? (
          // The label's hint; the value keeps its own full-name tooltip.
          <div
            className="min-w-0"
            title="The ad's name in Meta Ads Manager — usually the video"
          >
            <DetailItem label="Ad" value={metaAdName(metaAd, "Ad")} />
          </div>
        ) : null,
      },
      {
        // Campaigns the lead re-enquired through — may differ from the
        // campaign that originally created it.
        key: "reenquired",
        show:
          isFieldVisible("source.reenquired") &&
          (lead?.reenquiryCampaigns?.length ?? 0) > 0,
        wide: true,
        node: (
          <DetailItem
            label="Re-enquired via"
            value={(lead?.reenquiryCampaigns ?? [])
              .map(
                (c: { id: number; name: string | null; count: number }) =>
                  c.count > 1
                    ? `${c.name ?? "Unknown campaign"} ×${c.count}`
                    : c.name ?? "Unknown campaign"
              )
              .join(", ")}
          />
        ),
      },
      {
        key: "lastUpdated",
        show: isFieldVisible("source.lastUpdated"),
        node: (
          <DetailItem
            label="Last Updated"
            value={formatDate(activeData?.updatedAt)}
          />
        ),
      },
      {
        key: "dealCreated",
        show: isFieldVisible("source.dealCreated"),
        node: (
          <DetailItem
            label="Deal Created"
            value={child?.createdAt ? formatDate(child.createdAt) : "N/A"}
          />
        ),
      },
      {
        key: "leadCreated",
        show: isFieldVisible("source.leadCreated"),
        node: <DetailItem label="Lead Created" value={leadCreatedDisplay} />,
      },
      {
        key: "assignedTo",
        show:
          isFieldVisible("source.assignedTo") &&
          !!assignedUsers &&
          assignedUsers.length > 0,
        node: (
          <div className="flex flex-col">
            <p className="text-b4 text-[var(--color-black-6)]">Assigned to</p>
            <div className="flex items-center pr-1 py-0 mt-1">
              {assignedUsers?.slice(0, 4).map((user: any, index: number) => {
                const initials =
                  user?.name
                    ?.split(" ")
                    .filter(Boolean)
                    .map((part: string) => part[0]?.toUpperCase())
                    .slice(0, 2)
                    .join("") || "NA";

                const colors = avatarColors[index % avatarColors.length];

                return (
                  <Avatar
                    key={index}
                    className="size-8 -mr-1 border-2 border-white"
                    style={{ backgroundColor: colors.bg }}
                    title={user?.name}
                  >
                    <AvatarFallback
                      className="text-[14px] font-medium tracking-[-0.084px]"
                      style={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                      }}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                );
              })}
              {/* Past four, the rest used to vanish without a trace. */}
              {assignedUsers.length > 4 && (
                <span
                  className="grid size-8 -mr-1 place-items-center rounded-full border-2 border-white bg-[#f1f3f2] text-[12px] font-medium text-[var(--color-black-8)]"
                  title={assignedUsers
                    .slice(4)
                    .map((u: any) => u?.name)
                    .filter(Boolean)
                    .join(", ")}
                >
                  +{assignedUsers.length - 4}
                </span>
              )}
            </div>
          </div>
        ),
      },
    ].filter((c) => c.show),
    (c) => c.key,
  );

  return (
    <Card className="border-[#e6e8e7] rounded-[12px] gap-0 py-2.5 shadow-none">
      <CardHeader className="px-3 pb-0">
        <CardTitle className="text-[14px] font-medium text-black tracking-[-0.28px] leading-normal">
          Lead Source & Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pt-0">
        {/* Two columns below desktop: three left each phone cell ~100px.
            `wide` cells span the whole row (see `cells`). */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 lg:gap-x-6 pl-1">
          {cells.map((c) => (
            <div key={c.key} className={cn("min-w-0", c.wide && "col-span-full")}>
              {c.node}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
